import { Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, Observable, shareReplay } from 'rxjs';
import { Suggestion } from '@panaya/ai-chat/domain';

/**
 * Service that provides suggestions extracted from AI responses.
 * Acts as a single source of truth for suggestions state across the application.
 *
 * This service:
 * - Stores and emits suggestions via observable
 *
 */
@Injectable({ providedIn: 'root' })
export class AiChatSuggestionsProviderService {
    private readonly suggestionsSubject$ = new BehaviorSubject<Suggestion[]>([]);

    /**
     * Observable stream of suggestions extracted from AI responses.
     */
    public readonly suggestions$: Observable<Suggestion[]> = this.suggestionsSubject$.asObservable().pipe(
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        shareReplay(1)
    );

    /**
     * Stores suggestions and notifies all subscribers.
     * Called by interceptors when processing AI responses.
     *
     * @param suggestions - Array of Suggestion objects to store
     */
    public setSuggestions(suggestions: Suggestion[]): void {
        this.suggestionsSubject$.next(suggestions);
    }

    /**
     * Clears all suggestions and notifies subscribers.
     * Typically called when thread changes or suggestions are no longer relevant.
     */
    public clearSuggestions(): void {
        this.suggestionsSubject$.next([]);
    }
}
